import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import Aside from '../../../components/layout/asaid.component';
import Header from '../../../components/layout/header.component';
import Footer from '../../../components/layout/footer.component';
import Icon from '../../../components/forms/icon.component';
import CustomDataTable from '../../../components/datatable/datatable.component';
import useBgPages from '../../../hooks/use-bg-pages.hook';
import useRequest from '../../../hooks/use-request.hook';
import style from '../wa/components/templates-wa.module.scss';
import { AlertService } from '../../../services/alert.service';
import { TemplatesService } from '../../../services/templates.service';
import ActionsDropdown from '../../../components/datatable/actions-dropdown.component';
import LogData from '../../../components/logs/log-data.component';
import PrincipalCard from '../../../components/cards/principal-card.component';
import { useSession } from '../../../hooks/use-session.hook';
import { PROFILES } from '../../../constants';
import { useDarkMode } from '../../../hooks/use-dark-mode.hook';

function TemplatesWaPage() {
  useBgPages();
  const { t } = useTranslation();
  const { session } = useSession();
  const { call, isRequesting } = useRequest();
  const [rows, setRows] = useState<any>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [totalTemplates, seTotalTemplates] = useState(0);
  const [marketing, setMarketing] = useState(0);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const isDarkTheme = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { state: { wabaId, accountName } = {} } = location;

  const dataStatistics = [
    {
      label: t('templates.wa.templates-title'),
      total: totalTemplates,
      type: 'primary',
      icon: 'ki-whatsapp'
    },
    {
      label: t('templates.wa.marketing-name'),
      total: marketing,
      type: 'info',
      icon: 'ki-price-tag'
    }
  ];

  const columns = [
    {
      name: t('templates.wa.name'),
      sortable: true,
      selector: (r: any) => `${r.name} ${r.account.company.name} ${r.account.name}`,
      width: 5,
      cell: (row: any) => (
        <li className="list-group-item">
          <div className="d-flex flex-column">
            <div className="mw-200px mb-1 text-truncate">
              <strong className="fs-6 d-block">{t('templates.wa.name')}:</strong>
              <span className="ms-1">{row.name || t('general.no-info')}</span>
            </div>
            {session.profile === PROFILES.ROOT && (
              <>
                <div className="w-100 mb-1">
                  <strong className="fs-6">{t('blacklist-page.company-label')}:</strong>
                  <span className="ms-1">{row.account.company.name || t('general.no-info')}</span>
                </div>
                <div className="w-100 mb-1 d-flex align-items-center">
                  <strong className="fs-6">{t('blacklist-page.account-label')}:</strong>
                  <span className="ms-1">{row.account.name || t('general.no-info')}</span>
                </div>
              </>
            )}
          </div>
        </li>
      )
    },
    {
      name: t('templates.wa.language'),
      center: true,
      sortable: true,
      selector: (r: any) => `${r.language}`,
      width: 3,
      cell: (row: any) => {
        switch (row.language) {
          case 'es_MX':
            return (
              <img
                alt="imageCh"
                src={'/assets/media/flags/mexico.svg'}
                className="rounded-circle me-2"
                height={'16px'}
                title={`language-Spanish`}
              />
            );
          case 'en':
            return (
              <img
                alt="imageCh"
                src={'/assets/media/flags/united-states.svg'}
                className="rounded-circle me-2"
                height={'16px'}
                title={`language-English`}
              />
            );
        }
      }
    },
    {
      name: t('templates.wa.category'),
      sortable: true,
      selector: (r: any) => `${r.category}`,
      width: 15,
      cell: (row: any) => (
        <div>
          <h5>
            {row.category.toLowerCase().charAt(0).toUpperCase() +
              row.category.slice(1).toLowerCase()}
          </h5>
        </div>
      )
    },
    {
      name: t('templates.wa.status'),
      sortable: true,
      selector: (r: any) => `${r.status}`,
      width: 15,
      cell: (row: any) => {
        switch (row.status) {
          case 'APPROVED':
            return (
              <span className="badge text-dark fs-6">
                <Icon icon="ki-duotone ki-check-circle" size="fs-3" type="primary" />
                {t('templates.wa.approved')}
              </span>
            );
          case 'REJECTED':
            return (
              <span className="badge text-danger fs-6">
                <Icon icon="ki-duotone ki-minus-circle" size="fs-3" type="danger" />
                {t('templates.wa.rejected')}
              </span>
            );
          case 'PENDING':
            return (
              <span className="badge text-warning fs-6">
                <Icon icon="ki-duotone ki-dots-horizontal" size="fs-3" type="warning" />
                {t('templates.wa.pending')}
              </span>
            );
          default:
            return (
              <span className="badge text-secondary fs-6">
                <Icon icon="ki-duotone ki-question-circle" size="fs-3" type="secondary" />
                {t('templates.wa.unknown')}
              </span>
            );
        }
      }
    },
    {
      name: t('templates.wa.preview'),
      sortable: true,
      selector: (r: any) => `${r._id}`,
      width: 2,
      center: true,
      cell: (row: any) => (
        <button
          className="btn btn-light btn-sm hover-scale fw-bold"
          onClick={() => setSelectedRow(row)}
        >
          <Icon icon="ki-eye" size="fs-2" />
        </button>
      )
    },
    {
      name: t('templates.wa.actions'),
      sortable: true,
      selector: (r: any) => `${r._id}`,
      center: true,
      exportable: false,
      cell: (row: any) => {
        return (
          <ActionsDropdown
            delete={() => handleDeleteTemplate(row._id, row.name, 'deleteTemplateWA-9174562789')}
          />
        );
      }
    }
  ];

  const handleDeleteTemplate = async (id: string, name: string, buttonId: string) => {
    try {
      const confirmed = await AlertService.question(
        t('general.atention'),
        `¿Quiere eliminar tu plantilla?, ${name}`,
        null as any,
        'Eliminar'
      );
      if (!confirmed) {
        return;
      }

      const deletePromise = TemplatesService.deleteTemplateWa({ template: id });

      AlertService.toastLoading(
        deletePromise,
        t('templates.wa.loading-delete'),
        t('templates.wa.successs-delete'),
        t('templates.wa.error-delete')
      );

      await deletePromise;
      await getTemplates(wabaId);
      LogData(id, buttonId, window.location.href);
      setSelectedRow(null);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const getTemplates = async (wabaid: any) => {
    try {
      const data = await call(() => TemplatesService.getTemplatesAccountWa(wabaid));
      setRows(data);
      seTotalTemplates(data.length);
      setMarketing(data.filter((x: any) => x.category === 'MARKETING').length);
    } catch (err: any) {
      console.log('Error: ', err);
      AlertService.toastError(err?.error || 'Error');
    }
  };

  useEffect(() => {
    if (!wabaId) {
      AlertService.toastError(t('templates.wa.error.no-waba-account'));
      navigate('/templates-panel/accounts-wa', { replace: true });
      return;
    }
  }, [wabaId, navigate, t]);
  useEffect(() => {
    if (!wabaId) return;
    getTemplates(wabaId);
  }, []);

  const renderButtonsPreview = () => {
    const buttonsComponent = selectedRow?.components.find(
      (component: any) => component.type === 'BUTTONS'
    );
    const allButtons = buttonsComponent?.buttons || [];
    const visibleButtons = allButtons.slice(0, 2);
    const totalButtons = allButtons.length;

    return (
      <>
        {visibleButtons.map((btn: any, btnIndex: number) => (
          <div key={btnIndex} className="me-1">
            <hr className="my-0" />
            <div
              className={`${isDarkTheme ? style.preview_template_dark_btn : style.preview_template_btn} ${isDarkTheme ? style.preview_template_dark : style.preview_template}`}
              style={{ boxShadow: 'none' }}
            >
              {btn.type === 'URL' && (
                <>
                  <i className="bi-box-arrow-up-right"></i> {btn.text}
                </>
              )}
              {btn.type === 'QUICK_REPLY' && (
                <>
                  <i className="bi-reply"></i> {btn.text}
                </>
              )}
              {btn.type === 'QUICK_REPLY_DISABLE_MKT' && (
                <>
                  <i className="bi-reply"></i> {btn.text}
                </>
              )}
              {btn.type === 'PHONE_NUMBER' && (
                <>
                  <i className="bi-telephone"></i> {btn.text}
                </>
              )}
              {btn.type === 'COPY_CODE' && (
                <>
                  <i className="bi-clipboard"></i> {btn.text}
                </>
              )}
            </div>
          </div>
        ))}

        {totalButtons > 3 && (
          <div className="cursor-pointer" onClick={() => setShowAllOptions(true)}>
            <hr className="my-0" />
            <div
              className={`${isDarkTheme ? style.preview_template_dark_btn : style.preview_template_btn} ${isDarkTheme ? style.preview_template_dark : style.preview_template}`}
              style={{ boxShadow: 'none' }}
            >
              <i className="bi-list me-2"></i>
              {t('templates.wa.create.see-all-options')}
            </div>
          </div>
        )}
      </>
    );
  };

  // Funciones para formatear texto de WhatsApp

  function parseBold(text: string): string {
    return text.replace(/\*(.*?)\*/g, '<strong>$1</strong>');
  }

  function parseStrikethrough(text: string): string {
    return text.replace(/~(.*?)~/g, '<del>$1</del>');
  }

  function parseWhatsAppFormatting(text: string): string {
    let formatted = text;
    formatted = parseBold(formatted);
    formatted = parseStrikethrough(formatted);
    formatted = formatted.replace(/\n/g, '<br/>');
    return formatted;
  }

  return (
    <>
      <div className="page d-flex flex-row flex-column-fluid">
        <Aside />
        <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
          <Header />
          <div className="content fs-6 d-flex flex-column flex-column-fluid" id="kt_content">
            <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
              <div className="container-xxl">
                <PrincipalCard
                  dataStatistics={dataStatistics}
                  onClick={() =>
                    navigate('/templates/create-template-WA', {
                      state: { wabaId, accountName }
                    })
                  }
                  btnRefresh
                  onRefresh={async () => {
                    await getTemplates(wabaId);
                    AlertService.toastSuccess('¡ Éxito !, información actualizada.');
                  }}
                  btnBack
                  title={t('templates.templates-title')}
                  titleBolder={t('templates.wa.wa-title')}
                  subtitle={
                    t('templates.wa.subtitle') ||
                    'Personaliza y gestiona tus plantillas de WhatsApp'
                  }
                  breadcrumbItems={[
                    'templates.wa.tools',
                    'templates.templates-title',
                    'templates.wa.wa-account'
                  ]}
                  btnCreate
                  iconButton="ki-soft-2"
                  textButton={t('templates.wa.create-template')}
                />

                <div className="row g-xl-10">
                  <div className="col-xl-8 mb-5">
                    <div className="card">
                      <div className="card-header align-items-center border-0 mt-5">
                        <h3 className="card-title align-items-start flex-column animate-fadeInUp animate-delay-1">
                          <span className="fw-bolder fw-bold fs-2">
                            {t('templates.wa.templates-title')}
                          </span>
                          <span className="text-gray-400 mt-2 fw-semibold fs-6">
                            {t('templates.wa.registered')}
                          </span>
                        </h3>
                      </div>
                      <div className="card-body pt-1">
                        <CustomDataTable columns={columns} data={rows} isLoading={isRequesting} />
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-4">
                    <div className="sticky-box">
                      {!selectedRow ? (
                        <div className={isDarkTheme ? 'wrap-iphone-wa-dark' : 'wrap-iphone-wa'}>
                          <div className={`${style.bg_whatsApp}`}></div>
                        </div>
                      ) : (
                        <div className={isDarkTheme ? 'wrap-iphone-wa-dark' : 'wrap-iphone-wa'}>
                          <div className={`${style.bg_whatsApp}`}>
                            <div className={`${style.container_message}`}>
                              <div className={`${style.sticky}`}>
                                <div className="mt-3 me-2">
                                  <div
                                    className={`${isDarkTheme ? style.preview_template_dark : style.preview_template}`}
                                  >
                                    <div style={{ padding: '16px 16px 5px 16px' }}>
                                      {selectedRow.components.map(
                                        (component: any, index: number) => {
                                          if (component.type === 'HEADER') {
                                            if (component.format === 'TEXT') {
                                              return (
                                                <div
                                                  key={index}
                                                  className={`${isDarkTheme ? style.preview_template_dark_header : style.preview_template_header}`}
                                                >
                                                  {component.text}
                                                </div>
                                              );
                                            }
                                            if (component.format === 'IMAGE') {
                                              return (
                                                <div
                                                  key={index}
                                                  className={`${isDarkTheme ? style.preview_template_dark_header : style.preview_template_header}`}
                                                >
                                                  <div
                                                    className={`${isDarkTheme ? style.preview_template_dark_image : style.preview_template_image}`}
                                                    style={{
                                                      backgroundImage: `url(${component.example.header_handle[0]})`
                                                    }}
                                                  >
                                                    {!component.example.header_handle[0] && (
                                                      <i className="bi-card-image"></i>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            }
                                            if (component.format === 'VIDEO') {
                                              return (
                                                <div
                                                  key={index}
                                                  className={`${isDarkTheme ? style.preview_template_dark_header : style.preview_template_header}`}
                                                >
                                                  <div
                                                    className={`${isDarkTheme ? style.preview_template_dark_video : style.preview_template_video}`}
                                                  >
                                                    <i className="bi-play-circle-fill"></i>
                                                  </div>
                                                </div>
                                              );
                                            }
                                            if (component.format === 'DOCUMENT') {
                                              return (
                                                <div
                                                  key={index}
                                                  className={`${isDarkTheme ? style.preview_template_dark_header : style.preview_template_header}`}
                                                >
                                                  <div
                                                    className={`${isDarkTheme ? style.preview_template_dark_video : style.preview_template_video}`}
                                                  >
                                                    <i className="bi-filetype-pdf"></i>
                                                  </div>
                                                </div>
                                              );
                                            }
                                            if (component.format === 'LOCATION') {
                                              return (
                                                <div
                                                  key={index}
                                                  className={`${isDarkTheme ? style.preview_template_dark_header : style.preview_template_header}`}
                                                >
                                                  <div
                                                    className={`${isDarkTheme ? style.preview_template_dark_video : style.preview_template_video}`}
                                                  >
                                                    <i className="bi-geo-alt"></i>
                                                  </div>
                                                </div>
                                              );
                                            }
                                          }
                                          if (component.type === 'BODY') {
                                            return (
                                              <div
                                                key={index}
                                                dangerouslySetInnerHTML={{
                                                  __html: parseWhatsAppFormatting(
                                                    component.text || ''
                                                  )
                                                }}
                                              ></div>
                                            );
                                          }
                                          if (component.type === 'FOOTER') {
                                            return (
                                              <div
                                                key={index}
                                                className={`${isDarkTheme ? style.preview_template_dark_footer : style.preview_template_footer}`}
                                              >
                                                {component.text}
                                              </div>
                                            );
                                          }
                                          return null;
                                        }
                                      )}
                                    </div>
                                    {renderButtonsPreview()}
                                  </div>
                                </div>
                              </div>
                            </div>
                            {showAllOptions && (
                              <div
                                className={` shadow-sm ${isDarkTheme ? style.preview_template_dark_options : style.preview_template_options}`}
                              >
                                <div className="d-flex align-items-center my-2">
                                  <div onClick={() => setShowAllOptions(false)}>
                                    <i className="bi-x fs-2x ms-3 cursor-pointer"></i>
                                  </div>
                                  <div className="d-flex w-100 text-center justify-content-center align-items-center">
                                    <span className="me-5 text-center">Todas las opciones</span>
                                  </div>
                                </div>
                                {selectedRow.components
                                  .find((c: any) => c.type === 'BUTTONS')
                                  ?.buttons?.map((btn: any, index: number) => (
                                    <div key={index}>
                                      <div
                                        className={
                                          (isDarkTheme
                                            ? style.preview_template_dark_btn
                                            : style.preview_template_btn) +
                                          ' text-start ' +
                                          (isDarkTheme
                                            ? style.preview_template_dark
                                            : style.preview_template)
                                        }
                                        style={{ boxShadow: 'none' }}
                                      >
                                        {btn.type === 'URL' && (
                                          <>
                                            <i className="bi-box-arrow-up-right"></i> {btn.text}
                                          </>
                                        )}
                                        {btn.type === 'QUICK_REPLY' && (
                                          <>
                                            <i className="bi-reply"></i> {btn.text}
                                          </>
                                        )}
                                        {btn.type === 'QUICK_REPLY_DISABLE_MKT' && (
                                          <>
                                            <i className="bi-reply"></i> {btn.text}
                                          </>
                                        )}
                                        {btn.type === 'PHONE_NUMBER' && (
                                          <>
                                            <i className="bi-telephone"></i> {btn.text}
                                          </>
                                        )}
                                        {btn.type === 'COPY_CODE' && (
                                          <>
                                            <i className="bi-clipboard"></i> {btn.text}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
}

export default TemplatesWaPage;
